/**
 * 63.4 step 7 (ARCHITECTURE §5.13 "63.3 and 63.4, the design"; lane F): a blast reaches a structure by its shape — the
 * distance from where it went off to the nearest point of the structure's measured parts (core/hits.ts), over the
 * blast's damage circle — instead of from the structure's centre. A person-scale burst (a rocket's) is stopped by what
 * lies between, the shapes a bullet meets, as the people behind them are; a shell is not (its cover is the rim, people's
 * alone, as today). Used under the clock policy's `blastReach: 'shape'`; the turn game keeps the centre rule. Pure.
 */
import type { GameState, StructureState, Vec3 } from '../types';
import { vecAdd, vecLength, vecScale, vecSub } from '../math';
import { segmentBlocked, structureNearest, markedPartDistances } from './hits';
import { moduleDistances } from './moduleDamage';
import { personSetOf } from './scaleProfile';

/**
 * How far a blast is from a structure's shape, and whether something between stops it. `parts` (partial destruction step
 * 4, with the switch on): how far it is from each marked part (core/parts.ts), which each take the blast by their own.
 */
export interface StructureReach { dist: number; covered: boolean; parts?: number[]; /** 63.14 step 4: how far it is from each layout module (core/moduleDamage.ts moduleDistances), with modular buildings on; `tick` the match's, for the rubble it leaves */ modules?: number[]; tick?: number }

/**
 * The reach of a blast at `point` on each structure: by its shape (null when it has none — the caller measures from its
 * centre). For a person-scale burst (`family` 'person') the cover test runs from `coverFrom` (the burst's centre, a hair
 * off what it struck) to just short of the structure's nearest point, so its own shape never covers it. `coverWithin`
 * (DECISIONS 300's cost): a structure farther than it is not tested for cover — beyond the blast's widest circle it takes
 * nothing either way, and the test's segment across the planet swept the hit-shape cells on every burst.
 */
export function structureShapeReach(
  state: Pick<GameState, 'rocks' | 'flora' | 'structures' | 'planets' | 'config'> & Partial<Pick<GameState, 'tick' | 'turnNumber'>>, point: Vec3, family: 'shell' | 'person', coverFrom: Vec3 = point, coverWithin = Infinity,
): (s: StructureState) => StructureReach | null {
  const short = 0.05 * personSetOf(state).unitRadius;
  const partsOn = !!state.config?.partialDestruction;
  return s => {
    const near = structureNearest(point, s, state);
    if (!near) return null;
    const parts = partsOn ? markedPartDistances(point, s, state) : null;
    const modules = state.config?.modularBuildings ? moduleDistances(point, s, state) : null;
    const withParts = (r: StructureReach): StructureReach => ({ ...r, ...(parts ? { parts } : {}), ...(modules ? { modules, tick: state.tick ?? state.turnNumber } : {}) });
    if (family === 'shell' || near.dist > coverWithin) return withParts({ dist: near.dist, covered: false });
    const back = vecSub(coverFrom, near.at), len = vecLength(back);
    if (len <= short) return withParts({ dist: near.dist, covered: false });
    return withParts({ dist: near.dist, covered: segmentBlocked(coverFrom, vecAdd(near.at, vecScale(back, short / len)), s.planetIndex, state) });
  };
}
